import * as React from 'react';
import { useMemo } from 'react';

import clsxm from '@/lib/clsxm';
import { StaticComponent } from '@/lib/types';
import { useHeroContext } from '@/hooks/use-hero-context';

import { ColWidth } from '@/components/col';
import ContentSection, {
  ContentSectionProps,
} from '@/components/content-section';
import HygraphImage, { HygraphImageProps } from '@/components/hygraph-image';
import Row from '@/components/row';
import { TypographyColor } from '@/components/typography';

import { HorizontalAlignment } from '@/enums/horizontal-alignment';
import { ImageSizing } from '@/enums/image-sizing';
import { RowLayout } from '@/enums/row-layout';
import { Side } from '@/enums/side';
import { VerticalAlignment } from '@/enums/vertical-alignment';
import { VerticalSide } from '@/enums/vertical-side';
import DynamicStaticComponent from '@/hygraph/renderers/static-component.renderer';

export type ContentSectionWithImageRowProps = {
  content?: ContentSectionProps;
  image?: HygraphImageProps;
  contentBackgroundImage?: HygraphImageProps;
  imagePosition?: Side;
  imageOrder?: VerticalSide;
  imageVerticalAlign?: VerticalAlignment;
  imageSizing?: ImageSizing;
  useCheckmarkForLIs?: boolean;
  imageColWidth?: ColWidth;
  contentColWidth?: ColWidth;
  rowClassName?: string;
  headerClassName?: string;
  contentAlignment?: HorizontalAlignment;
  buttonAlignment?: HorizontalAlignment;
  containerMargin?: string;
  rowLayout?: RowLayout;
  staticComponentAfter?: StaticComponent;
};

export default function ContentSectionWithImageRow(
  props: ContentSectionWithImageRowProps
) {
  const rowLayout = useMemo<RowLayout>(() => {
    if (props.rowLayout || props.imageSizing === ImageSizing.IMAGE_75) {
      return RowLayout.TWO_COLUMN_WIDE_RIGHT;
    }
    return RowLayout.TWO_COLUMN_HALFS;
  }, [props.rowLayout, props.imageSizing]);

  const imagePosition = props.imagePosition || Side.RIGHT;
  const imageOrder = props.imageOrder || VerticalSide.TOP;

  const { isHero } = useHeroContext();

  const hasTwoImages = !!props.contentBackgroundImage;

  const baseColClassName = 'flex justify-center h-full w-full lg:items-center';

  const { color, ...contentProps } = (
    props.content ? { ...props.content } : {}
  ) as ContentSectionProps;
  if (contentProps) {
    contentProps.buttonAlign = props.buttonAlignment;
    contentProps.headerClassName = props.headerClassName;
  }

  const isOverflow = props.imageSizing === ImageSizing.OVERFLOW;
  const isMatchHeight =
    isOverflow || props.imageSizing === ImageSizing.MATCH_HEIGHT;
  const image75 = props.imageSizing === ImageSizing.IMAGE_75;

  const content = (
    <div
      key='content'
      className={clsxm(
        'relative flex-col overflow-hidden lg:pl-8 lg:pr-11',
        baseColClassName,
        isMatchHeight && (hasTwoImages ? 'lg:py-10' : 'lg:my-1'),
        hasTwoImages && 'justify-end rounded-2xl pl-4 pr-2'
      )}
    >
      {props.contentBackgroundImage && (
        <HygraphImage
          className='absolute inset-0 z-0 h-full w-full'
          imgClassName='mx-auto w-full h-full'
          {...props.contentBackgroundImage}
        />
      )}
      {hasTwoImages ? (
        <ContentSection
          {...contentProps}
          containerClassName='z-10 mt-32 lg:mt-0'
          overline={false}
          color={TypographyColor.PRIMARY_CONTRAST}
        />
      ) : (
        <ContentSection
          {...contentProps}
          containerClassName='z-10'
          color={color}
        />
      )}
      <div className='w-full'>
        {props.staticComponentAfter && (
          <div className={clsxm('mt-6')}>
            <DynamicStaticComponent {...props.staticComponentAfter} />
          </div>
        )}
      </div>
    </div>
  );

  const image = (
    <ContentSectionWithImageImage
      key='image'
      image={props.image}
      imagePosition={props.imagePosition}
      imageSizing={props.imageSizing}
      imageVerticalAlign={props.imageVerticalAlign}
      className={clsxm(
        baseColClassName,
        'justify-center',
        isMatchHeight && 'relative'
      )}
      imgClassName={clsxm(hasTwoImages && 'rounded-2xl')}
    />
  );

  if (hasTwoImages) {
    return (
      <Row
        layout={RowLayout.FULL_WIDE}
        className={clsxm(
          props.imageVerticalAlign === VerticalAlignment.BOTTOM &&
            'py-0 lg:py-0',
          isHero && 'py-0 lg:py-0',
          props.rowClassName,
          isOverflow && 'mb-[-3rem]',
          image75 && 'lg:px-16'
        )}
      >
        <div className='grid gap-4 lg:auto-rows-[1fr] lg:grid-cols-2'>
          {imagePosition === Side.LEFT ? [image, content] : [content, image]}
        </div>
      </Row>
    );
  }

  return (
    <Row
      layout={rowLayout}
      className={clsxm(
        // 'flex lg:flex-row',
        // 'grid auto-rows-[1fr] lg:auto-rows-auto ',
        // 'grid-flow-rows grid-auto-rows-[1fr]',
        props.imageVerticalAlign === VerticalAlignment.BOTTOM && 'py-0 lg:py-0',
        isHero && 'py-0 lg:py-0',
        props.rowClassName,
        isOverflow && 'mb-[-3rem]',
        image75 && 'lg:px-16',
        imageOrderClassnameMap[imageOrder]
      )}
    >
      {imagePosition === Side.LEFT ? [image, content] : [content, image]}
    </Row>
  );
}

export type ContentSectionWithImageImagesProps = {
  className?: string;
  imgClassName?: string;
  image?: HygraphImageProps;
  imagePosition?: Side;
  imageSizing?: ImageSizing;
  imageVerticalAlign?: VerticalAlignment;
};
const ContentSectionWithImageImage = ({
  image,
  imagePosition,
  imageSizing,
  imageVerticalAlign,
  className,
  imgClassName,
}: ContentSectionWithImageImagesProps) => {
  if (!image || !imagePosition) {
    return <></>;
  }

  const isOverflow = imageSizing === ImageSizing.OVERFLOW;
  const isMatchHeight = isOverflow || imageSizing === ImageSizing.MATCH_HEIGHT;

  const { ...rest } = image;

  return (
    <div
      className={clsxm(
        isMatchHeight && 'h-full',
        imageVerticalAlign === VerticalAlignment.BOTTOM,
        className
      )}
    >
      <HygraphImage
        className={clsxm(
          (isMatchHeight || isOverflow) && 'z-1 inset-0 lg:absolute lg:h-full'
        )}
        imgClassName={clsxm(
          isMatchHeight && 'lg:max-h-full lg:max-w-full',
          isOverflow && 'lg:max-h-[120%] lg:max-w-none',
          (isMatchHeight || isOverflow) && 'lg:w-auto lg:mx-auto',
          imgClassName
        )}
        {...rest}
      />
    </div>
  );
};

const imageOrderClassnameMap: Record<VerticalSide, string> = {
  [VerticalSide.TOP]: 'flex-col',
  [VerticalSide.BOTTOM]: 'flex-col-reverse',
};
